// Roadmap 2: one Ledgerate AI conversation in the builder. Claude replies
// with the complete list of code steps; this hook turns that into a draft
// (sources → code steps → output), runs it in the sandbox on the sample
// files, and asks Claude to fix a failing step (up to MAX_FIXES times, with
// sanitised error text only). The user fills in any values, then accepts or
// discards the draft. Replaces useAiChat (Roadmap 1 step tools) for now.
//
// Every message is cleaned on the device first (promptMask.js): data values
// become placeholders such as {{text_1}}, kept in a vault for the whole
// conversation, and fill in the step values Claude declares with that name.
// The shape sent with it also says which source columns hold those values
// and each source column's format profile (data/formatProfile.js).
//
// While Claude works, the server streams progress (`progress`): its stage,
// the reply's message so far and how many steps it has started.
import { useCallback, useEffect, useRef, useState } from "react";
import { deleteAiConversation, reportBlockedMessage, sendAiMessage } from "../../../api/aiServices";
import { CODE_STEP, tableLabel } from "../../../engine";
import { emptyValue } from "../builder/ValueForm";
import { dataHost } from "../data/dataHost";
import { fileRefsOf, previewRun } from "../data/preview";
import { buildDraft, fillHiddenValues } from "./codeDraft";
import { describeForAi, valueFields } from "./codeShape";
import { createVault, maskPrompt } from "./promptMask";
import { loadTextRules } from "./textRules";

const MAX_FIXES = 2;

const errorText = (error) => error?.response?.data?.message || error?.message || error?.error || "The AI request failed. Please try again.";

// Column names and letters in the shape sent to the AI (the only words
// besides plain English a message may use). Mirrors shapeNames() in server/ai/guard.js.
const shapeNames = (shape) => [
  ...shape.sources.flatMap((s) => s.columns.flatMap((c) => [c.name, c.letter])),
  ...shape.steps.flatMap((s) => (s.columns || []).map((c) => c.name)),
];

// Drafts run in their own scope, so the trail keeps showing the workflow's results.
const runDraft = async (workflow, files) => {
  const run = await previewRun(workflow, files, { scope: "draft", trigger: "ai" });
  if (run.stale) throw new Error("A newer run replaced this one. Try again.");
  return run;
};
const dropDraftResults = () => dataHost.dropScope("draft").catch(() => {});

// The format profile of every source column with a sample file.
const profilesOf = (workflow, files) => dataHost.columnProfiles(workflow, fileRefsOf(files)).catch(() => ({}));

// Where the text values hidden in this message appear in the sample files.
const placeholdersOf = (workflow, files, hidden) => {
  const values = hidden.filter((entry) => entry.kind === "text").map(({ token, value }) => ({ token, value }));
  return values.length ? dataHost.valueColumns(workflow, fileRefsOf(files), values).catch(() => []) : Promise.resolve([]);
};

const isMissing = (value) => value === undefined || value === null || (Array.isArray(value) ? !value.length : String(value).trim() === "");

// `conversation` is a ref shared with the editor: saving the workflow ends
// the conversation on the server and clears it.
export const useCodeChat = ({ workflow, files, conversation }) => {
  const [items, setItems] = useState([]); // [{ role: "user" | "assistant" | "status" | "error", text }]
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null); // { workflow, run }
  const [progress, setProgress] = useState(null); // { stage, message, step } while Claude works
  const event = useRef(null); // "accepted" | "discarded", told with the next message
  const vault = useRef(createVault()); // placeholder -> real value, never sent
  const request = useRef(null); // AbortController of the request in flight

  const say = (item) => setItems((current) => [...current, item]);

  // One request to the server, with its progress shown as it streams.
  const ask = useCallback(async (payload) => {
    const controller = new AbortController();
    request.current = controller;
    setProgress({ stage: "sending" });
    const onProgress = (name, data) =>
      setProgress((current) => {
        if (name === "stage") return data.stage === "retrying" ? { stage: "retrying" } : { ...current, stage: data.stage };
        if (name === "message") return { ...current, message: data.text };
        if (name === "step") return { ...current, step: data.step };
        return current;
      });
    try {
      return await sendAiMessage(payload, { onProgress, signal: controller.signal });
    } catch (error) {
      if (controller.signal.aborted) throw new Error("Stopped. Ledgerate AI's answer was cancelled.");
      throw error;
    } finally {
      request.current = null;
      setProgress(null);
    }
  }, []);

  // Leaving the builder cancels a request in flight.
  useEffect(() => () => request.current?.abort(), []);

  const send = useCallback(
    async (text, { mode, focusStepId }) => {
      if (!text.trim() || busy) return;
      setBusy(true);
      try {
        // Continue on the pending draft, or start from the workflow as it is now.
        let current = draft?.workflow ?? workflow;
        const currentRun = draft?.run ?? (await runDraft(current, files));
        const profiles = await profilesOf(current, files);
        const shape = describeForAi(current, currentRun, { focusStepId, profiles });
        const fileKeys = Object.values(files).map((file) => file.key);
        const masked = await maskPrompt(text, {
          rules: await loadTextRules(),
          vault: vault.current,
          columnNames: shapeNames(shape),
          findDataWords: async (words) => new Set(await dataHost.dataWords(fileKeys, words)),
        });
        if (masked.error) {
          say({ role: "user", text });
          say({ role: "error", text: masked.message });
          reportBlockedMessage(masked.rule);
          return;
        }
        say({ role: "user", text, sent: masked.text, hidden: masked.hidden });
        const placeholders = await placeholdersOf(current, files, masked.hidden);
        let response = await ask({
          conversationId: conversation.current,
          workflowId: workflow._id,
          projectId: workflow.projectID, // only checked by the server, never sent to Claude
          mode,
          text: masked.text,
          workflow: placeholders.length ? { ...shape, placeholders } : shape,
          event: event.current,
        });
        event.current = null;

        for (let fixes = 0; ; fixes++) {
          conversation.current = response.conversationId;
          const { reply, stopReason } = response;
          if (reply?.message) say({ role: "assistant", text: reply.message });
          if (stopReason === "refusal") say({ role: "error", text: "The AI declined this request." });
          if (stopReason === "max_tokens") say({ role: "error", text: "The answer was cut off. Try a smaller request." });
          if (!reply?.steps) break;

          const proposed = fillHiddenValues(buildDraft(current, reply), vault.current, reply);
          setProgress({ stage: "testing" });
          const run = await runDraft(proposed, files).finally(() => setProgress(null));
          const failed = run.results.find((r) => r.status === "error");
          const checkFailed = run.results.find((r) => r.status === "success" && r.failedChecks?.length);
          // Code that reads values the user hasn't filled in yet can fail for
          // that reason alone, so those failures aren't sent back to fix.
          const waitingForValues = valueFields(proposed).some((field) => isMissing(field.value));
          if ((failed || checkFailed) && !waitingForValues && fixes < MAX_FIXES) {
            const stepName = tableLabel(proposed, (failed || checkFailed).stepId);
            say({
              role: "status",
              text: failed ? `${stepName} failed on your sample. Asking Ledgerate AI to fix it…` : `A check on ${stepName} failed on your sample. Asking Ledgerate AI to review it…`,
            });
            current = proposed;
            response = await ask({
              conversationId: conversation.current,
              workflowId: workflow._id,
              projectId: workflow.projectID, // only checked by the server, never sent to Claude
              mode,
              event: "fix",
              workflow: describeForAi(proposed, run, { profiles }),
            });
            continue;
          }
          setDraft({ workflow: proposed, run });
          break;
        }
      } catch (error) {
        say({ role: "error", text: errorText(error) });
      } finally {
        setBusy(false);
      }
    },
    [ask, busy, conversation, draft, files, workflow],
  );

  // A value typed into the draft's form. Kept in the draft itself, so when
  // the user asks Claude for changes, it carries over into the new draft.
  const setValue = useCallback((stepId, name, value) => {
    setDraft((current) =>
      current && {
        ...current,
        workflow: {
          ...current.workflow,
          steps: current.workflow.steps.map((step) => (step.id === stepId ? { ...step, params: { ...step.params, values: { ...step.params.values, [name]: value } } } : step)),
        },
      },
    );
  }, []);

  // Returns { steps, outputs, description }. Values left empty are saved
  // empty ("" or no table rows).
  const accept = useCallback(
    () => {
      const steps = draft.workflow.steps.map((step) => {
        const fields = step.params?.valueFields || [];
        if (step.type !== CODE_STEP || !fields.length) return step;
        const filled = Object.fromEntries(fields.map((field) => [field.name, step.params.values?.[field.name] ?? emptyValue(field)]));
        return { ...step, params: { ...step.params, values: filled } };
      });
      const { outputs, description } = draft.workflow;
      setDraft(null);
      dropDraftResults();
      event.current = "accepted";
      return { steps, outputs, description };
    },
    [draft],
  );

  const discard = useCallback(() => {
    setDraft(null);
    dropDraftResults();
    event.current = "discarded";
  }, []);

  // Stops the request in flight (the server cancels it too).
  const cancel = useCallback(() => request.current?.abort(), []);

  // New conversation: forget it on the server too.
  const reset = useCallback(() => {
    request.current?.abort();
    if (conversation.current) deleteAiConversation(conversation.current).catch(() => {});
    conversation.current = null;
    event.current = null;
    vault.current = createVault();
    setDraft(null);
    dropDraftResults();
    setItems([]);
  }, [conversation]);

  return { items, busy, progress, draft, send, cancel, setValue, accept, discard, reset, vault: vault.current };
};
